<template>
  <el-container>
    <el-header>
      <div class="header-container">
        <div class="search-container">
          <el-input :placeholder="showPublished ? '搜索课程名称、作业名称' : '搜索姓名、邮箱、电话、账号'"
                    v-model="searchQuery"
                    clearable
                    prefix-icon="el-icon-search"
                    @input="handleSearch" />
        </div>
        <div class="action-buttons">
          <el-button type="primary"
                     @click="openPublishModal">
            发布作业
          </el-button>
          <el-button type="primary"
                     @click="togglePublishedView">
            {{ showPublished ? '查看学生信息' : '查看已发布作业' }}
          </el-button>
        </div>
      </div>
    </el-header>

    <el-main style="background-color: #ffffff; border-radius: 20px">
      <!-- 学生信息卡片布局 -->
      <div v-if="!showPublished">
        <div class="student-cards-container"
             v-if="paginatedData.length">
          <div class="student-card"
               v-for="item in paginatedData"
               :key="item.id">
            <div class="card-content">
              <div class="avatar-section">
                <img :src="item.id % 2 === 0 ? require('@/assets/male-avatar.png') : require('@/assets/female-avatar.png')" class="avatar" />
                <div class="student-name">{{ item.userName }}</div>
              </div>
              <div class="info-section">
                <div class="student-info">
                  <div class="info-row">
                    <span>ID: {{ item.id }}</span>
                    <el-button type="text"
                               class="logout-btn"
                               @click="confirmDelete(item)">注销账号</el-button>
                  </div>
                  <div class="info-row">账号：{{ item.accountNumber }}</div>
                  <div class="info-row">电话：{{ item.phone }}</div>
                  <div class="info-row">邮箱：{{ item.email }}</div>
                </div>
              </div>
            </div>
            <el-button type="primary"
                       class="edit-btn"
                       @click="openEditModal(item)">编辑</el-button>
          </div>
        </div>

        <div v-else
             class="no-data">
          <p>没有数据</p>
        </div>

        <el-pagination v-if="filteredData.length > pageSize"
                       @current-change="handleCurrentChange"
                       :current-page="currentPage"
                       :page-size="pageSize"
                       layout="total, prev, pager, next"
                       :total="filteredData.length"
                       class="pagination">
        </el-pagination>
      </div>

      <!-- 已发布作业卡片布局 -->
      <div v-else>
        <div class="homework-cards-container"
             v-if="publishedHomeworks.length">
          <div class="homework-card"
               v-for="item in paginatedPublishedData"
               :key="item.id">
            <div class="card-header">
              <span class="id-text">ID: {{ item.id }}</span>
              <div class="action-icons">
                <i class="el-icon-delete"
                   @click="confirmDeleteHomework(item)"
                   title="删除"></i>
              </div>
            </div>
            <div class="card-content">
              <div class="homework-image"
                   @click="previewImage(item.url)">
                <img :src="item.url ? `/resources/assignHomework/${item.url}` : '/assets/default-homework.png'"
                     alt="作业示例" />
              </div>
              <div class="homework-info">
                <div class="info-item">
                  <span class="label">课程名称：</span>
                  <span class="value">{{ item.courseName }}</span>
                </div>
                <div class="info-item">
                  <span class="label">作业名称：</span>
                  <span class="value">{{ item.homeworkName }}</span>
                </div>
                <div class="info-item">
                  <span class="label">发布教师：</span>
                  <span class="value">{{ item.teachName }}老师</span>
                </div>
                <div class="info-item">
                  <span class="label">发布时间：</span>
                  <span class="value">{{ formatDate(item.updateTime) }}</span>
                </div>
              </div>
            </div>
            <el-button type="primary"
                       class="edit-btn"
                       @click="openEditHomeworkModal(item)">编辑</el-button>
          </div>
        </div>

        <div v-else
             class="no-data">
          <p>{{ searchQuery ? '未找到匹配的作业' : '暂无已发布作业' }}</p>
        </div>

        <el-pagination v-if="publishedHomeworks.length > publishedPageSize"
                       @current-change="handlePublishedCurrentChange"
                       :current-page="publishedCurrentPage"
                       :page-size="publishedPageSize"
                       layout="total, prev, pager, next, jumper"
                       :total="publishedHomeworks.length"
                       class="pagination">
        </el-pagination>
      </div>
    </el-main>

    <!-- 发布作业弹出框 -->
    <el-dialog title="发布作业"
               :visible.sync="isPublishModalVisible"
               :close-on-click-modal="false"
               width="500px">
      <el-form>
        <el-form-item label="课程名称">
          <el-select v-model="selectedCourse"
                     placeholder="请选择对应作业的课程"
                     style="width: 100%">
            <el-option v-for="course in courseList"
                       :key="course"
                       :label="course"
                       :value="course">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="作业名称">
          <el-input v-model="homeworkTitle"
                    placeholder="请输入作业名称" />
        </el-form-item>
        <el-form-item label="作业示范">
          <el-upload class="upload-demo"
                     drag
                     action="#"
                     :auto-upload="false"
                     :on-change="handleUploadChange"
                     :show-file-list="false">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">点击或拖动文件到这里上传</div>
          </el-upload>
          <div v-if="uploadedFile"
               style="margin-top: 10px;">
            已选择：{{ uploadedFile.name }}
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer"
            class="dialog-footer">
        <el-button @click="cancelPublish">取消</el-button>
        <el-button type="primary"
                   @click="publishHomework"
                   :loading="publishing">
          发布作业
        </el-button>
      </span>
    </el-dialog>

    <!-- 编辑学生信息弹窗 -->
    <el-dialog title="编辑学生信息"
               :visible.sync="isEditModalVisible"
               :close-on-click-modal="false"
               width="500px">
      <el-form label-width="80px">
        <el-form-item label="账号">
          <el-input v-model="editAccount" disabled />
        </el-form-item>
        <el-form-item label="姓名">
          <el-input v-model="editName" />
        </el-form-item>
        <el-form-item label="电话">
          <el-input v-model="editPhone" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="editEmail" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="isEditModalVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEdit" :loading="saving">保存</el-button>
      </span>
    </el-dialog>

    <!-- 其他对话框 -->
    <el-dialog :visible.sync="previewVisible"
               :close-on-click-modal="true"
               width="800px"
               center
               append-to-body>
      <img :src="previewImageUrl"
           style="width: 100%; max-height: 80vh; object-fit: contain;"
           alt="作业预览" />
    </el-dialog>
     <!-- 编辑作业模态框 -->
     <el-dialog 
      title="编辑作业"
      :visible.sync="isEditHomeworkModalVisible"
      :close-on-click-modal="false">
      <el-form>
        <el-form-item label="课程名称">
          <el-select 
            v-model="editHomeworkData.courseName" 
            placeholder="请选择课程"
            style="width: 100%">
            <el-option
              v-for="course in courseList"
              :key="course"
              :label="course"
              :value="course">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="作业名称">
          <el-input v-model="editHomeworkData.homeworkName" />
        </el-form-item>
        <el-form-item label="作业文件">
          <el-upload
            class="upload-demo"
            drag
            action="#"
            :auto-upload="false"
            :on-change="handleEditUploadChange"
            :show-file-list="false">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">点击或拖动文件到这里上传</div>
          </el-upload>
          <div v-if="editHomeworkData.url || editHomeworkData.newFile" 
               class="preview-container" 
               style="margin-top: 10px;">
            <div class="preview-wrapper">
              <img 
                :src="previewUrl"
                style="width: 100px; height: 100px; object-fit: cover;"
              />
              <div class="preview-actions">
                <span class="preview-text">
                  {{ editHomeworkData.newFile ? '新选择：' + editHomeworkData.newFile.name : '当前图片' }}
                </span>
                <el-button 
                  type="text" 
                  class="delete-btn"
                  @click="removeNewFile"
                  v-if="editHomeworkData.newFile">
                  <i class="el-icon-delete"></i>
                </el-button>
              </div>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="isEditHomeworkModalVisible = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="saveHomeworkEdit"
          :loading="saving">
          保存
        </el-button>
      </span>
    </el-dialog>
  </el-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StudentManagement',
  data() {
    return {
      searchQuery: '',
      isPublishModalVisible: false,
      isEditModalVisible: false,
      homeworkTitle: '',
      uploadedFile: null,
      editAccount: '',
      editName: '',
      editPhone: '',
      editEmail: '',
      currentPage: 1,
      pageSize: 6, // 学生信息每页显示6个
      publishedPageSize: 6, // 作业信息每页显示4个
      data: [],
      editId: '',
      courseList: [],
      selectedCourse: '',
      publishing: false,
      showPublished: false,
      publishedHomeworks: [],
      publishedCurrentPage: 1,
      isEditHomeworkModalVisible: false,
      editHomeworkData: {
        id: '',
        courseName: '',
        homeworkName: '',
        url: '',
        newFile: null
      },
      previewVisible: false,
      previewImageUrl: '',
      saving: false,
      screenWidth: window.innerWidth
    };
  },
  computed: {
    filteredData() {
      if (!this.searchQuery) return this.data;
      const query = this.searchQuery.toLowerCase();
      return this.data.filter(item =>
        item.userName.toLowerCase().includes(query) ||
        item.email.toLowerCase().includes(query) ||
        item.phone.includes(query) ||
        item.accountNumber.toLowerCase().includes(query)
      );
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredData.slice(start, start + this.pageSize);
    },
    paginatedPublishedData() {
      const start = (this.publishedCurrentPage - 1) * this.publishedPageSize;
      return this.filteredPublishedData.slice(start, start + this.publishedPageSize);
    },
    filteredPublishedData() {
      if (!this.searchQuery) return this.publishedHomeworks;
      const query = this.searchQuery.toLowerCase();
      return this.publishedHomeworks.filter(item =>
        item.courseName.toLowerCase().includes(query) ||
        item.homeworkName.toLowerCase().includes(query)
      );
    },
    previewUrl() {
      if (this.editHomeworkData.newFile) {
        return URL.createObjectURL(this.editHomeworkData.newFile);
      }
      return this.editHomeworkData.url ? `/resources/assignHomework/${this.editHomeworkData.url}` : '';
    }
  },
  methods: {
    fetchData() {
      const accountNumber = localStorage.getItem('userAccountNumber');
      axios.get(`api/studentUser/selectAll?accountNumber=${accountNumber}`)
        .then(response => {
          if (response.data.code === 0) {
            this.data = response.data.data;
          } else {
            this.$message.error(response.data.msg)
            console.error(response.data.msg);
          }
        })
        .catch(error => {
          this.$message.error('获取数据失败:' + error)
          console.error('获取数据失败:', error);
        });
    },
    handleSearch() {
      if (this.showPublished) {
        this.publishedCurrentPage = 1;
      } else {
        this.currentPage = 1;
      }
    },
    openPublishModal() {
      this.isPublishModalVisible = true;
      this.homeworkTitle = '';
      this.uploadedFile = null;
      this.selectedCourse = '';
      this.fetchCourseList();
    },
    handleUploadChange(file) {
      const isValidFile = this.validateFile(file.raw);
      if (isValidFile) {
        this.uploadedFile = file.raw;
      }
    },
    async publishHomework() {
      if (!this.selectedCourse) {
        this.$message.warning('请选择课程');
        return;
      }
      if (!this.homeworkTitle) {
        this.$message.warning('请输入作业名称');
        return;
      }
      if (!this.uploadedFile) {
        this.$message.warning('请上传作业示范文件');
        return;
      }

      const formData = new FormData();
      formData.append('file', this.uploadedFile);
      formData.append('courseName', this.selectedCourse);
      formData.append('homeworkName', this.homeworkTitle);

      const teachAccount = localStorage.getItem('userAccountNumber');
      if (teachAccount) {
        formData.append('teachAccount', teachAccount);
      }

      this.publishing = true;

      try {
        const response = await axios.post('/api/assignHomework/save', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.code === 0) {
          this.$message.success('作业发布成功');
          this.isPublishModalVisible = false;
          this.resetPublishForm();
          if (this.showPublished) {
            this.fetchPublishedHomeworks();
          }
        } else {
          this.$message.error(response.data.msg || '发布失败');
        }
      } catch (error) {
        console.error('发布作业失败:', error);
        this.$message.error('发布失败，请稍后重试');
      } finally {
        this.publishing = false;
      }
    },
    cancelPublish() {
      this.$confirm('确认取消布作业？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.resetPublishForm();
        this.isPublishModalVisible = false;
      }).catch(() => { });
    },
    resetPublishForm() {
      this.selectedCourse = '';
      this.homeworkTitle = '';
      this.uploadedFile = null;
    },
    validateFile(file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过10MB');
        return false;
      }
      return true;
    },
    openEditModal(item) {
      this.editId = item.id;
      this.editAccount = item.accountNumber;
      this.editName = item.userName;
      this.editPhone = item.phone;
      this.editEmail = item.email;
      this.isEditModalVisible = true;
    },
    saveEdit() {
      const params = {
        id: this.editId,
        userName: this.editName,
        phone: this.editPhone,
        email: this.editEmail
      };

      axios.post('api/studentUser/updateStudentUsers', params)
        .then(response => {
          if (response.data.code === 0) {
            this.$message.success('更新成功');
            this.isEditModalVisible = false;
            this.fetchData();
          } else {
            this.$message.error(response.data.msg || '更新失败');
          }
        })
        .catch(error => {
          console.error('更新失败:', error);
          this.$message.error('更新失败，请稍后重试');
        });
    },
    confirmDelete(item) {
      this.$confirm(`确认注销账号 ${item.accountNumber} 吗?`, '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        axios.post(`api/studentUser/deleteById?id=${item.id}`)
          .then(response => {
            if (response.data.code === 0) {
              this.$message.success('注销成功');
              this.fetchData();
            } else {
              this.$message.error(response.data.msg || '注销失败');
            }
          })
          .catch(error => {
            console.error('注销失败:', error);
            this.$message.error('注销失败，请稍后重试');
          });
      }).catch(() => {
        this.$message.info('已取消注销');
      });
    },
    handleCurrentChange(page) {
      this.currentPage = page;
    },
    fetchCourseList() {
      axios.get('/api/courseTeaching/allName')
        .then(response => {
          if (response.data.code === 0) {
            this.courseList = response.data.data.map(item => item.courseName);
          } else {
            this.$message.error(response.data.msg || '获取课程列表失败');
          }
        })
        .catch(error => {
          console.error('获取课程列表失败:', error);
          this.$message.error('获取课程列表失败');
        });
    },
    togglePublishedView() {
      this.showPublished = !this.showPublished;
      this.searchQuery = '';
      this.currentPage = 1;
      this.publishedCurrentPage = 1;

      if (this.showPublished) {
        this.fetchPublishedHomeworks();
      } else {
        this.fetchData();
      }
    },
    fetchPublishedHomeworks() {
      const accountNumber = localStorage.getItem('userAccountNumber');
      axios.get(`/api/assignHomework/all?teachAccount=${accountNumber}`)
        .then(response => {
          if (response.data.code === 0) {
            this.publishedHomeworks = response.data.data;
            const maxPage = Math.ceil(this.publishedHomeworks.length / this.publishedPageSize);
            if (this.publishedCurrentPage > maxPage) {
              this.publishedCurrentPage = 1;
            }
          } else {
            this.$message.error(response.data.msg || '获取已发布作业失败');
          }
        })
        .catch(error => {
          console.error('获取已发布作业失败:', error);
          this.$message.error('获取已发布作业失败');
        });
    },
    openEditHomeworkModal(homework) {
      this.editHomeworkData = {
        id: homework.id,
        courseName: homework.courseName,
        homeworkName: homework.homeworkName,
        url: homework.url,
        newFile: null
      };
      this.isEditHomeworkModalVisible = true;
      this.fetchCourseList();
    },
    handleEditUploadChange(file) {
      const isValidFile = this.validateFile(file.raw);
      if (isValidFile) {
        this.editHomeworkData.newFile = file.raw;
        this.$message.success('新图片已选择');
      }
    },
    async saveHomeworkEdit() {
      if (!this.editHomeworkData.courseName || !this.editHomeworkData.homeworkName) {
        this.$message.warning('请填写完整信息');
        return;
      }

      this.saving = true;
      const formData = new FormData();
      formData.append('id', this.editHomeworkData.id);
      formData.append('courseName', this.editHomeworkData.courseName);
      formData.append('homeworkName', this.editHomeworkData.homeworkName);

      if (this.editHomeworkData.newFile) {
        formData.append('file', this.editHomeworkData.newFile);
      } else {
        try {
          const response = await fetch(`/resources/assignHomework/${this.editHomeworkData.url}`);
          const blob = await response.blob();
          const file = new File([blob], this.editHomeworkData.url, { type: blob.type });
          formData.append('file', file);
        } catch (error) {
          console.error('获取原有图片失败:', error);
          this.$message.error('获取原有图片失败，请重试');
          this.saving = false;
          return;
        }
      }

      try {
        const response = await axios.post('/api/assignHomework/updateByid', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.code === 0) {
          this.$message.success('更新成功');
          this.isEditHomeworkModalVisible = false;
          this.fetchPublishedHomeworks();
        } else {
          this.$message.error(response.data.msg || '更新失败');
        }
      } catch (error) {
        console.error('更新失败:', error);
        this.$message.error('更新失败，请稍后重试');
      } finally {
        this.saving = false;
      }
    },
    confirmDeleteHomework(homework) {
      this.$confirm(`确认删除作业 "${homework.homeworkName}" 吗?`, '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        axios.post(`/api/assignHomework/deleteById?id=${homework.id}`)
          .then(response => {
            if (response.data.code === 0) {
              this.$message.success('删除成功');
              this.fetchPublishedHomeworks();
            } else {
              this.$message.error(response.data.msg || '删除失败');
            }
          })
          .catch(error => {
            console.error('删除失败:', error);
            this.$message.error('删除失败，请稍后重试');
          });
      }).catch(() => {
        this.$message.info('已取消删除');
      });
    },
    handlePublishedCurrentChange(page) {
      this.publishedCurrentPage = page;
    },
    previewImage(fileUrl) {
      if (!fileUrl) return;
      this.previewImageUrl = `/resources/assignHomework/${fileUrl}`;
      this.previewVisible = true;
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    removeNewFile() {
      this.editHomeworkData.newFile = null;
      this.$message.success('已移除新选择的文件');
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
    }
  },
  mounted() {
    this.fetchData();
    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // 移除监听器
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 0 20px;
}

.search-container {
  flex: 1;
  max-width: 619px;
}

.search-container :deep(.el-input) {
  width: 100%;
}

.search-container :deep(.el-input__inner) {
  height: 40px;
  line-height: 40px;
  border-radius: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: -20px;
}

.action-buttons .el-button {
  border-radius: 20px;
  padding: 10px 20px;
}

/* 响应式卡片容器 */
.student-cards-container {
  display: grid;
  gap: 20px;
  padding: 20px;
  background-color: #ffffff;
  grid-template-columns: repeat(3, 1fr);
}

.homework-cards-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px;
  background-color: #ffffff;
  justify-content: flex-start;
}

.homework-card {
  background-color: #e9f6ff;
  border-radius: 8px;
  padding: 20px;
  width: calc(33.33% - 14px);
  max-width: 450px;
  min-width: 280px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

/* 笔记本屏幕显示2列，并缩小元素 */
@media screen and (max-width: 1739px) and (min-width: 1201px) {
  .student-cards-container {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    padding: 20px;
  }

  .homework-card {
    width: calc(50% - 10px);
    max-width: 450px;
    min-width: 280px;
  }

  .homework-image {
    width: 220px;
    height: 110px;
  }

  .homework-info {
    font-size: 13px;
  }

  .homework-info .label {
    min-width: 65px;
  }

  .id-text {
    font-size: 14px;
  }

  .info-item .value {
    font-size: 13px;
  }

  .card-content {
    gap: 15px;
  }

  .avatar {
    width: 60px;
    height: 60px;
  }

  .student-name {
    font-size: 16px;
  }

  .info-item {
    font-size: 14px;
  }

  .info-item .label {
    min-width: 70px;
  }
}

/* 小型笔记本屏幕 */
@media screen and (max-width: 1200px) and (min-width: 992px) {
  .student-cards-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 20px;
  }

  .homework-card {
    width: calc(50% - 10px);
    max-width: 450px;
    min-width: 280px;
  }

  .homework-image {
    width: 150px;
    height: 105px;
  }

  .homework-info {
    font-size: 12px;
  }

  .homework-info .label {
    min-width: 60px;
  }

  .id-text {
    font-size: 13px;
  }

  .card-content {
    gap: 12px;
  }
}

/* 平板屏幕显示1列 */
@media screen and (max-width: 991px) {
  .student-cards-container {
    grid-template-columns: 1fr;
  }

  .homework-card {
    width: 100%;
    max-width: 400px;
    margin: 0 auto 20px;
  }
}

/* 卡片基础样式 */
.student-card {
  background-color: #e9f6ff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  width: 455px;
  flex-direction: column;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.id-text {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.action-icons {
  display: flex;
  gap: 15px;
}

.action-icons i {
  font-size: 18px;
  color: #2ea2fa;
  cursor: pointer;
}

.action-icons i:hover {
  color: #1890ff;
}

.card-content {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  flex: 1;
}

.homework-image {
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
  width: 160px;
  height: 118px;
  overflow: hidden;
  cursor: pointer;
}

.homework-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.homework-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-item {
  display: flex;
  align-items: flex-start;
}

.info-item .label {
  color: #666;
  min-width: 80px;
}

.info-item .value {
  color: #333;
  flex: 1;
  word-break: break-all;
  white-space: nowrap;
  text-align: justify;
}

.edit-btn {
  width: 90%;
  margin: 0 auto;
}

.avatar-section {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border-right: 2px solid #2ea2fa;
  padding-right: 20px;
}

.avatar {
  border-radius: 50%;
  object-fit: cover;
  height: 90px;
  width: 90px;
}

.student-name {
  font-family: PingFang SC, PingFang SC;
  font-weight: 500;
  text-align: center;
}

.info-section {
  flex-grow: 1;
}

.student-info {
  flex-grow: 1;
}

.info-row {
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
}

.logout-btn {
  color: #2ea2fa;
  border: 1px solid #2ea2fa;
  border-radius: 20px;
  background-color: transparent;
  padding: 2px;
  display: block;
}

.no-data {
  text-align: center;
  padding: 40px;
  color: #909399;
}

/* 发布作业对话框样式 */
.upload-demo {
  width: 100%;
}

.el-upload {
  width: 100%;
}

.el-upload-dragger {
  width: 100%;
  height: 120px;
}

.dialog-footer {
  text-align: right;
  margin-top: 20px;
}

.el-dialog__body {
  padding: 20px 30px;
}

.el-form-item__label {
  font-weight: bold;
}

.table-container {
  margin-top: 20px;
}

/* 分页组件样式 */
.pagination {
  margin-top: 20px;
  text-align: right;
  padding-right: 20px;
}
</style>
